import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonationSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: null });
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ phase: "success", data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "expired", data });
          return;
        }
        if (attempts++ < 6) {
          timer = setTimeout(poll, 2000);
        } else {
          setState({ phase: "timeout", data });
        }
      } catch (e) {
        if (attempts++ < 4) {
          timer = setTimeout(poll, 2000);
        } else {
          setState({ phase: "error", data: null });
        }
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-ink-900 text-white">
      <div className="pointer-events-none absolute inset-0 bg-grain opacity-30 mix-blend-overlay" />
      <div className="pointer-events-none absolute -left-32 top-1/4 h-72 w-72 rounded-full bg-gold/15 blur-[140px]" />
      <div className="pointer-events-none absolute -right-32 bottom-1/4 h-72 w-72 rounded-full bg-gold/10 blur-[160px]" />

      <div className="mx-auto flex min-h-screen max-w-3xl flex-col items-center justify-center px-6 py-20 text-center">
        <motion.img
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          src="https://customer-assets.emergentagent.com/job_crash-proof-movement/artifacts/j30fas3r_40BB10F5-2D42-4AAE-9504-A5FC7C4F46EA.PNG"
          alt="Mr. Green Bay"
          className="mb-10 h-20 w-20 rounded-md object-contain"
        />

        {state.phase === "checking" && (
          <>
            <i className="fa-solid fa-circle-notch fa-spin text-3xl text-gold" />
            <h1 className="mt-8 font-display text-3xl text-white md:text-4xl">Confirming your sponsorship…</h1>
            <p className="mt-3 text-zinc-400">Hang tight — verifying with Stripe.</p>
          </>
        )}

        {state.phase === "success" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-4 flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-10 bg-gold/60" /> Sponsorship Confirmed <span className="h-px w-10 bg-gold/60" />
            </div>
            <h1 className="font-display text-4xl font-bold leading-tight text-white md:text-6xl">
              You just <span className="text-gradient-gold">moved the needle.</span>
            </h1>
            <p className="mx-auto mt-6 max-w-xl text-lg text-zinc-300">
              {state.data?.tier && <span className="block">Tier: <span className="text-gold">{state.data.tier}</span></span>}
              {state.data?.amount_total && (
                <span className="block">Amount: <span className="text-gold">${(state.data.amount_total / 100).toFixed(2)}</span></span>
              )}
              <span className="mt-4 block text-zinc-400">A receipt is on its way to your email. The brotherhood thanks you.</span>
            </p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/"
                data-testid="success-back-home"
                className="btn-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
              >
                <i className="fa-solid fa-arrow-left mr-2" /> Back To The Movement
              </Link>
              <a
                href="https://a.co/d/0inHxgPY"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="success-get-book"
                className="btn-ghost-gold rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
              >
                <i className="fa-brands fa-amazon mr-2" /> Get The Book
              </a>
            </div>
          </motion.div>
        )}

        {(state.phase === "expired" || state.phase === "timeout" || state.phase === "error") && (
          <>
            <i className="fa-solid fa-circle-exclamation text-3xl text-gold" />
            <h1 className="mt-8 font-display text-3xl text-white md:text-4xl">We couldn't confirm yet</h1>
            <p className="mt-3 max-w-md text-zinc-400">
              {state.phase === "expired"
                ? "Your checkout session expired. Try sponsoring again."
                : "Still waiting on Stripe. If you completed payment, a confirmation will arrive by email shortly. Reach out at info@mrgreenbay.com if anything looks off."}
            </p>
            <Link
              to="/"
              data-testid="success-fallback-home"
              className="btn-gold mt-8 rounded-full px-7 py-4 text-sm font-semibold uppercase tracking-wider"
            >
              Back Home
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
